import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Database, FileText, Zap } from "lucide-react";

interface CycleInfoCardProps {
  data: any;
}

/* -------------------- Component -------------------- */

export const CycleInfoCard = ({ data: details }: CycleInfoCardProps) => {
  const cycleDetails = [
    {
      label: "Billing Period",
      value:
        details?.cycleDetails?.billingPeriodStart &&
        details?.cycleDetails?.billingPeriodEnd
          ? `${details.cycleDetails.billingPeriodStart} - ${details.cycleDetails.billingPeriodEnd}`
          : details?.cycleDetails?.billingPeriodStart || "NA",
    },
    { label: "Category", value: details?.cycleDetails?.categoryDisplay },
    { label: "Sub-Category", value: details?.cycleDetails?.subCategoryDisplay },
    {
      label: "Bill Distribution Days",
      value: details?.cycleDetails?.billDistributionDays,
    },
    { label: "Premise", value: details?.cycleDetails?.premiseCount },
    { label: "Consumer", value: details?.cycleDetails?.consumerCount },
    { label: "Meter", value: details?.cycleDetails?.meter },
  ];

  const billRunDetails = [
    { label: "Run By", value: details?.billRunDetails?.runBy },
    { label: "Run Date", value: details?.billRunDetails?.runDate },
    { label: "Last Run By", value: details?.billRunDetails?.lastRunBy },
    { label: "Last Run Date", value: details?.billRunDetails?.lastRunDate },
    {
      label: "Days Since Last Run",
      value: details?.billRunDetails?.daysSinceLastRun,
    },
    { label: "Approved By", value: details?.billRunDetails?.approvedBy },
    { label: "Approved On", value: details?.billRunDetails?.approvedOn },
    {
      label: "Approver's Comment",
      value: details?.billRunDetails?.approverComment,
    },
  ];

  const SectionHeader = ({
    title,
    icon: Icon,
  }: {
    title: string;
    icon: React.ElementType;
  }) => (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-4 w-4 text-blue-600" />
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Database className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">
                {details?.cycleName}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                # {details?.cycleDetails?.cycleId}
              </p>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">
            {details?.executionType}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Cycle Details */}
        <div>
          <SectionHeader title="Cycle Details" icon={FileText} />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-3">
            {cycleDetails.map((item, idx) => (
              <ValueLabel key={idx} label={item.label} value={item.value} />
            ))}
          </div>
        </div>

        {/* Bill Run Details */}
        <div>
          <SectionHeader title="Bill Run Details" icon={Zap} />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-3">
            {billRunDetails.map((item, idx) => (
              <ValueLabel key={idx} label={item.label} value={item.value} />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
